/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Which UI this user sees: 'legacy' (today's) or 'v2' (the enterprise shell).
 *
 * The server resolves it on /permissions/me as `ui`: the user's own choice,
 * else the tenant default, else legacy. The value is mirrored to localStorage
 * so the first paint after a reload already knows the answer instead of
 * flashing the legacy UI while the snapshot loads. Once the snapshot arrives
 * it wins.
 *
 * REACT_APP_UI_V2_ENABLED=false is the build-level kill switch: it forces
 * legacy for everyone regardless of stored preferences.
 */

import { useCallback, useEffect, useMemo } from "react";
import { useAuth } from "auth/Auth";
import { usePermissionsContext } from "auth/PermissionsProvider";
import {
  UI_LEGACY,
  UI_V2,
  UI_VERSION_STORAGE_KEY,
  VERSIONS,
  isUiV2Enabled,
  readStoredUiVersion,
  resolveUiVersion,
  writeStoredUiVersion,
} from "./uiVersion";

// Re-export local bindings (not `export { … } from`) so CRA/webpack still
// sees the named exports when this module is in an import cycle with auth.
export {
  UI_LEGACY,
  UI_V2,
  UI_VERSION_STORAGE_KEY,
  isUiV2Enabled,
  readStoredUiVersion,
  resolveUiVersion,
};

export default function useUiVersion() {
  const { axiosInstance } = useAuth();
  const { snapshot, ready, refresh } = usePermissionsContext();
  const serverUi = snapshot?.ui ?? null;
  const enabled = isUiV2Enabled();

  const version = resolveUiVersion({
    serverUi: ready ? serverUi : null,
    stored: readStoredUiVersion(),
    enabled,
  });

  useEffect(() => {
    if (ready && serverUi && VERSIONS.has(serverUi.version)) {
      writeStoredUiVersion(serverUi.version);
    }
  }, [ready, serverUi]);

  /**
   * Switch look. `null` drops the user's own choice so the tenant default
   * applies. The theme, globals and shell all change, so this reloads.
   */
  const setUiVersion = useCallback(
    async (next, { reload = true } = {}) => {
      const body = next ? { version: next } : { clear_version: true };
      const { data } = await axiosInstance.patch(
        "/users/me/ui-preferences",
        body
      );
      writeStoredUiVersion(data?.version ?? next);
      if (reload && typeof window !== "undefined") window.location.reload();
      else refresh?.();
      return data;
    },
    [axiosInstance, refresh]
  );

  /** v2 shell state (sidebar collapse, favourites...). `null` removes a key. */
  const setUiPref = useCallback(
    async (key, value) => {
      const { data } = await axiosInstance.patch("/users/me/ui-preferences", {
        prefs: { [key]: value },
      });
      refresh?.();
      return data;
    },
    [axiosInstance, refresh]
  );

  return useMemo(
    () => ({
      uiVersion: version,
      isV2: version === UI_V2,
      enabled,
      ready,
      userChoice: serverUi?.user_choice ?? null,
      tenantDefault: serverUi?.tenant_default ?? UI_LEGACY,
      // True for an Australian Real Estate tenant: no classic screens exist.
      locked: Boolean(serverUi?.locked),
      uiPrefs: serverUi?.prefs ?? {},
      seats: snapshot?.seats ?? [],
      setUiVersion,
      setUiPref,
    }),
    [version, enabled, ready, serverUi, snapshot, setUiVersion, setUiPref]
  );
}
